'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Surface the error in browser console so we can actually see what broke.
    console.error('[app/error.tsx]', error);

    // ── ChunkLoadError auto-recovery ──────────────────────────────────────
    // После каждого деплоя Next пересобирает chunks с новыми хешами.
    // Если у юзера открыта вкладка со старой HTML → при ленивой подгрузке
    // он попытается достать `06uoes9ytewhj.js` — которого уже нет → ошибка.
    // Лечим жёстко: один раз делаем hard reload, добавив `?v=ts` в URL,
    // чтобы обойти любой HTML-кеш (в т.ч. браузерный и SW).
    const msg = `${error?.message || ''} ${error?.stack || ''}`;
    const isChunkError = /ChunkLoadError|Loading chunk .* failed|Failed to load chunk|Loading CSS chunk/i.test(msg);
    if (isChunkError && typeof window !== 'undefined') {
      // sessionStorage-флаг, чтобы не уйти в бесконечный цикл, если проблема
      // не в деплое, а в реальной сети / CSP / чём-то постоянном.
      const key = 'chunk_error_reload_at';
      const last = Number(sessionStorage.getItem(key) || 0);
      if (Date.now() - last > 30_000) {
        sessionStorage.setItem(key, String(Date.now()));
        const u = new URL(window.location.href);
        u.searchParams.set('__r', String(Date.now()));
        window.location.replace(u.toString());
        return;
      }
    }

    // Best-effort: ship to server log too.
    fetch('/api/debug/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: error?.message,
        stack: error?.stack,
        digest: error?.digest,
        url: typeof window !== 'undefined' ? window.location.href : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      }),
    }).catch(() => {});
  }, [error]);

  return (
    <div className="h-full flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-danger-light flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-danger" />
        </div>
        <h2 className="text-xl font-bold text-neutral-800 mb-2">Что-то пошло не так</h2>
        <p className="text-sm text-neutral-500 mb-6">
          Произошла непредвиденная ошибка. Попробуйте обновить страницу.
        </p>
        <div className="flex items-center gap-2 justify-center">
          <Button onClick={reset}>Попробовать снова</Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Перезагрузить
          </Button>
        </div>
        <button
          onClick={() => setShowDetails((v) => !v)}
          className="mt-6 inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-neutral-600"
        >
          {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          {showDetails ? 'Скрыть детали' : 'Показать детали ошибки'}
        </button>
        {showDetails && (
          <pre className="mt-3 text-left text-[11px] leading-snug font-mono bg-neutral-900 text-neutral-100 p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
            {error?.message || 'Unknown error'}
            {error?.digest ? `\n\ndigest: ${error.digest}` : ''}
            {error?.stack ? `\n\n${error.stack}` : ''}
          </pre>
        )}
      </div>
    </div>
  );
}
